import * as React from "react"
import { useState, useEffect } from "react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  alpha,
} from "@mui/material"
import { useNavigate } from "react-router-dom"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import ImageIcon from "@mui/icons-material/Image"
import { downscale, dataURLToBlob } from "../../utils/imageFunctions"
import { useDropzone } from "react-dropzone"

function UploadTab() {
  const [image, setImage] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const processFile = (file) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      downscale(reader.result, 1024).then((resizedImage) => {
        setImage(resizedImage)
        const blob = dataURLToBlob(resizedImage)
        const newFile = new File([blob], file.name, { type: blob.type })
        setSelectedFile(newFile)
      })
    }
    reader.readAsDataURL(file)
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "image/*": [] },
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        processFile(acceptedFiles[0])
      }
    },
  })

  const fetchClimb = async () => {
    if (selectedFile) {
      console.log(selectedFile)
      setLoading(true)
      const formData = new FormData()
      formData.append("image", selectedFile)

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL || ""}/api/find_climb`,
          {
            method: "POST",
            body: formData,
          }
        )
        const json = await response.json()
        const climbId = json.uuid
        console.info("climbId: ", climbId)
        navigate(`/climb/${climbId}`)
        console.info(json)
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    fetchClimb()
  }, [selectedFile])

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
      }}
    >
      {/* Dropzone */}
      <Box
        {...getRootProps()}
        sx={(theme) => ({
          width: "100%",
          maxWidth: 640,
          minHeight: image ? "auto" : 280,
          border: `2px dashed ${
            isDragActive
              ? theme.palette.primary.main
              : alpha(theme.palette.text.secondary, 0.25)
          }`,
          borderRadius: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.2s ease",
          bgcolor: isDragActive
            ? alpha(theme.palette.primary.main, 0.06)
            : "transparent",
          "&:hover": {
            borderColor: theme.palette.primary.main,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
          },
          overflow: "hidden",
          position: "relative",
        })}
      >
        <input {...getInputProps()} />

        {image ? (
          <Box
            component="img"
            src={image}
            alt="Uploaded photo"
            sx={{
              width: "100%",
              display: "block",
              borderRadius: 2,
            }}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
              py: 4,
            }}
          >
            <CloudUploadIcon
              sx={(theme) => ({
                fontSize: 56,
                color: isDragActive
                  ? theme.palette.primary.main
                  : alpha(theme.palette.text.secondary, 0.35),
                transition: "color 0.2s ease",
              })}
            />
            <Typography variant="body1" color="text.secondary">
              {isDragActive
                ? "Drop your image here"
                : "Drag & drop a climbing wall photo"}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", opacity: 0.6 }}>
              or click to browse
            </Typography>
          </Box>
        )}
      </Box>

      {/* Loading indicator */}
      {loading && (
        <Card sx={{ width: "100%", maxWidth: 640 }}>
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              py: 4,
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="body2" color="text.secondary">
              Identifying climb...
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

export default UploadTab
